import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'
import { Slider } from '../forms/Slider'
import { ToggleSwitch } from '../forms/Toggle'

// Resumen + Avanzado dimensions (design/components/bot-config-advanced.html).
// PipelineDiagram (Resumen) is INFORMATIONAL, not editable — navigable text, not
// an image — making clear the safety gates run before any token is spent and
// that a handoff catches every dead end. Avanzado exposes the technical levers WITH their cost: ModelPickerCards
// (with $/1M tokens visible next to the name), TemperatureSlider, SandboxToggle.
// CostBreakdownSidebar translates the choices into an estimated per-conversation
// cost, with the cache saving. Sandbox-on must be paired with a Banner by the app.

// --- Resumen · PipelineDiagram ----------------------------------------------

export interface PipelineStep {
  name: string
  sub: string
  tone?: 'sens' | 'brand' | 'default'
}

// THE ORCHESTRATOR PATH, which is what every production bot runs
// (agente-runtime-model.md §1 "Class A" + §2). This strip used to read
// `priority → classify → retrieve → generate → guard → handoff`: four of those
// six boxes described the CLASSIC pipeline, whose steps live after the
// orchestrator hands the whole turn to the flow and returns — code that does not
// execute for any bot with an orchestrator. It was the operator's mental model
// of the product, and it was of a dead code path.
//
// What actually happens, in order: the no-LLM GATES (priority phrases, consent,
// pause, out-of-hours) → Descubrir, the route node whose options the author
// wrote, classified by Haiku → the FLOW itself, asking and answering with the
// knowledge base → the guard over the text the model generated → and the handoff
// that catches every dead end so nothing ends in silence.
const PIPELINE_KEYS = ['gates', 'descubrir', 'flow', 'guard', 'handoff'] as const
const PIPELINE_TONE: Record<string, PipelineStep['tone']> = { gates: 'sens', flow: 'brand' }

export interface PipelineDiagramProps {
  /** Defaults to the fixed engine pipeline. */
  steps?: PipelineStep[]
  className?: string
}

/** Read-only Resumen strip of the orchestrator path (gates → … → handoff)
 *  rendered as navigable text, not an image. Informational only — nothing here
 *  is editable. */
export function PipelineDiagram({ steps, className }: PipelineDiagramProps) {
  const { t } = useTranslation('components')
  const resolved: PipelineStep[] =
    steps ??
    PIPELINE_KEYS.map((k) => ({
      name: t(`advanced.pipeline.steps.${k}.name`),
      sub: t(`advanced.pipeline.steps.${k}.sub`),
      tone: PIPELINE_TONE[k] ?? 'default',
    }))

  return (
    <ol
      aria-label={t('advanced.pipeline.label')}
      className={cn('flex flex-wrap items-stretch gap-1.5', className)}
    >
      {resolved.map((s, i) => (
        <li key={i} className="flex items-stretch gap-1.5">
          <span className="flex flex-col gap-0.5 rounded-lg border border-line bg-surface px-2.5 py-1.5">
            <span
              className={cn(
                'text-[11px] font-bold',
                s.tone === 'sens' ? 'text-sens' : s.tone === 'brand' ? 'text-link' : 'text-text',
              )}
            >
              {s.name}
            </span>
            <span className="font-mono text-[9.5px] text-text-3">{s.sub}</span>
          </span>
          {i < resolved.length - 1 && (
            <span aria-hidden className="self-center text-text-3">
              →
            </span>
          )}
        </li>
      ))}
    </ol>
  )
}

// --- Avanzado · ModelPickerCards --------------------------------------------

export interface ModelOption {
  value: string
  name: string
  description: string
  /** Human cost string, e.g. "$3 / $15 por 1M tokens". */
  cost: string
}

export interface ModelPickerCardsProps {
  value: string
  onChange: (value: string) => void
  models: ModelOption[]
  className?: string
}

/** Radiogroup of LLM model cards for Avanzado, each showing its $/1M-token
 *  cost beside the name. Roving tabindex; arrow keys move AND select. */
export function ModelPickerCards({ value, onChange, models, className }: ModelPickerCardsProps) {
  const { t } = useTranslation('components')
  const refs = useRef<(HTMLButtonElement | null)[]>([])

  function move(currentIndex: number, delta: number) {
    const next = (currentIndex + delta + models.length) % models.length
    const opt = models[next]
    if (!opt) return
    onChange(opt.value)
    refs.current[next]?.focus()
  }

  return (
    <div
      role="radiogroup"
      aria-label={t('advanced.modelGroupLabel')}
      className={cn('flex flex-col gap-2', className)}
    >
      {/* Keep the first card focusable when `value` matches no model, so the
          radiogroup never traps the keyboard (roving tabindex). */}
      {models.map((m, i) => {
        const checked = m.value === value
        const focusable = checked || (i === 0 && !models.some((x) => x.value === value))
        return (
          <button
            key={m.value}
            ref={(el) => {
              refs.current[i] = el
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={focusable ? 0 : -1}
            onClick={() => onChange(m.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                e.preventDefault()
                move(i, 1)
              } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                e.preventDefault()
                move(i, -1)
              }
            }}
            className={cn(
              'flex flex-col gap-1 rounded-xl border p-3 text-left transition-colors',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue',
              checked
                ? 'border-2 border-brand-blue bg-[color-mix(in_srgb,var(--brand-blue)_6%,transparent)]'
                : 'border-input-line hover:border-text-3',
            )}
          >
            <span className="flex items-baseline gap-2">
              <span className="text-[13px] font-bold text-text">{m.name}</span>
              <span className="ml-auto font-mono text-[11px] font-semibold tabular-nums text-text-2">
                {m.cost}
              </span>
            </span>
            <span className="text-[11.5px] leading-snug text-text-2">{m.description}</span>
          </button>
        )
      })}
    </div>
  )
}

// --- Avanzado · TemperatureSlider -------------------------------------------

export interface TemperatureSliderProps {
  /** 0–1. */
  value: number
  onChange: (value: number) => void
  className?: string
}

/** Slider for the model-temperature lever in Avanzado: 0–1 in 0.1 steps, with
 *  Preciso/Creativo end marks. */
export function TemperatureSlider({ value, onChange, className }: TemperatureSliderProps) {
  const { t } = useTranslation('components')
  return (
    <Slider
      value={value}
      onChange={onChange}
      min={0}
      max={1}
      step={0.1}
      label={t('advanced.temperature.label')}
      formatValue={(v) => v.toFixed(1)}
      marks={[t('advanced.temperature.low'), t('advanced.temperature.high')]}
      {...(className ? { className } : {})}
    />
  )
}

// --- Avanzado · SandboxToggle -----------------------------------------------

export interface SandboxToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  className?: string
}

/** Switch for sandbox mode in Avanzado. Renders only the toggle — the page
 *  must pair sandbox-on with a warning Banner. */
export function SandboxToggle({ checked, onChange, className }: SandboxToggleProps) {
  const { t } = useTranslation('components')
  return (
    <ToggleSwitch
      checked={checked}
      onChange={onChange}
      label={t('advanced.sandbox.label')}
      description={t('advanced.sandbox.description')}
      {...(className ? { className } : {})}
    />
  )
}

// --- Avanzado · CostBreakdownSidebar ----------------------------------------

export interface CostLine {
  label: string
  value: string
  /** Render the value in success colour (e.g. a saving). */
  positive?: boolean
}

export interface CostBreakdownSidebarProps {
  perConversationUsd: number
  lines: CostLine[]
  cacheSavingPct: number
  className?: string
}

/** Sidebar translating the Avanzado choices into an estimated USD cost per
 *  conversation: itemised lines plus the prompt-cache saving percentage. */
export function CostBreakdownSidebar({
  perConversationUsd,
  lines,
  cacheSavingPct,
  className,
}: CostBreakdownSidebarProps) {
  const { t } = useTranslation('components')
  return (
    <div className={cn('rounded-xl border border-line bg-surface p-4', className)}>
      <div className="flex items-baseline gap-1.5">
        <span className="text-[26px] font-extrabold tabular-nums text-text">
          ${perConversationUsd.toFixed(3)}
        </span>
        <span className="text-[11px] text-text-3">{t('advanced.cost.perConversation')}</span>
      </div>
      <p className="mt-1 text-[11px] text-text-2">
        {t('advanced.cost.cacheNote', { pct: cacheSavingPct })}
      </p>
      <div className="mt-3 flex flex-col">
        {lines.map((l, i) => (
          <div
            key={i}
            className="flex items-center justify-between border-t border-line py-1.5 text-[12px] first:border-t-0"
          >
            <span className="text-text-2">{l.label}</span>
            <span
              className={cn(
                'font-mono font-semibold tabular-nums',
                l.positive ? 'text-ok' : 'text-text',
              )}
            >
              {l.value}
            </span>
          </div>
        ))}
        <div className="flex items-center justify-between border-t border-line py-1.5 text-[12px]">
          <span className="text-text-2">{t('advanced.cost.cacheSaving')}</span>
          <span className="font-mono font-semibold tabular-nums text-ok">-{cacheSavingPct}%</span>
        </div>
      </div>
    </div>
  )
}
